import { PrivateAddressPanel } from './GroupPage'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ApiError, type NodeInfo } from '../../api'
import { POLL_MS, getAsOf, relayKeys, type LoadPoint, type NodeMetrics, type RelayRoute, type StatsRange } from './api'
import { bps, clock, ago, plural } from './format'
import { LineChart, onAxis } from './LineChart'
import { noteRelayAsOf, noteRelayFailure, type RelayLive } from './live'
import { Kpi, Meter } from './nodeParts'
import { nodeLegs } from './nodes'
import { RolesRelay } from './NodeRoles'
import { NodeFailoverPanel } from './NodeFailover'
import { RelayNextStep } from './RelayKey'
import { errorText } from './queries'
import { issueWhy, nodeIssues } from './states'
import { Empty, ErrorBox, NetPanel, SkeletonRows } from './ui'

/*
 * Node card › Overview (proto nodecard/Overview.tsx): the load now (CPU,
 * memory, load, traffic, legs, streams, clock), whether the peers list and the
 * routes are applied, why it is degraded, its roles and relay, and the history
 * from GET /api/admin/nodes/{id}/metrics?range= (15m from the center's memory
 * at 5 s; longer from the database). A missing step breaks the line.
 */

const RANGES: { id: StatsRange; label: string }[] = [
  { id: '15m', label: '15 min' },
  { id: '1h', label: '1 h' },
  { id: '6h', label: '6 h' },
  { id: '24h', label: '24 h' },
  { id: '7d', label: '7 d' },
  { id: '30d', label: '30 d' },
]

const dash = <span className="text-faint">—</span>

export function NodeOverview({ n, live, routes, relayOn }: { n: NodeInfo; live: RelayLive; routes: RelayRoute[]; relayOn: boolean }) {
  const on = n.status === 'online'
  const l = on ? n.load : undefined
  const c = on ? n.clock : undefined
  const issues = nodeIssues(n)
  const off = on ? 'no host report' : 'no report'
  const r = n.relay
  // NodeRelay.legs (API-UI) when the center sends it: known without host/1 too
  const lg = nodeLegs(n)
  return (
    <div className="flex flex-col gap-4">
      <NetPanel
        title="Now"
        testId="node-now"
        aside={
          <span className="num text-xs text-muted">
            {on ? 'reported every 5 s' : n.status === 'revoked' ? 'revoked' : `no report since ${clock(n.last_seen_at)} (${ago(n.last_seen_at)})`}
          </span>
        }
      >
        <div className="grid grid-cols-2 gap-x-6 gap-y-4 md:grid-cols-4 xl:grid-cols-7">
          <Kpi
            label="CPU"
            testId="kpi-cpu"
            tone={l && l.cpu_pct >= 85 ? 'warn' : undefined}
            value={
              l ? (
                <span className="flex items-center gap-2">
                  <span className="w-12">{Math.round(l.cpu_pct)}%</span>
                  <Meter pct={l.cpu_pct} width={40} />
                </span>
              ) : (
                dash
              )
            }
            sub={l ? `${l.cpus} cores` : off}
          />
          <Kpi label="Memory" testId="kpi-mem" value={l ? `${Math.round(l.mem_pct)}%` : dash} sub={l ? 'used' : off} />
          <Kpi label="Load 1 min" value={l ? l.load1.toFixed(2) : dash} sub={l ? `of ${l.cpus} cores` : off} />
          <Kpi label="Traffic" testId="kpi-traffic" value={l ? <span className="whitespace-nowrap">↑{bps(l.tx_bps)}</span> : dash} sub={l ? `out · in ↓${bps(l.rx_bps)}` : off} />
          <Kpi
            label="Legs up"
            testId="kpi-legs"
            value={lg.known && on ? `${lg.legsUp}/${lg.legs}` : dash}
            tone={lg.known && on && lg.legs > lg.legsUp ? 'warn' : undefined}
            sub={lg.known && on ? (lg.legs > lg.legsUp ? `${lg.legs - lg.legsUp} not up` : lg.legs ? 'all up' : 'no relay legs') + (lg.source === 'relay' ? ' · relay reports' : '') : off}
          />
          <Kpi
            label="Streams"
            testId="kpi-streams"
            value={
              l ? (
                <span className="whitespace-nowrap">
                  {l.streams_ingress}
                  <span className="text-muted"> · </span>
                  {l.streams_egress}
                </span>
              ) : on ? (
                n.streams
              ) : (
                dash
              )
            }
            sub={l ? 'ingress · egress (relay)' : on ? 'live, placed here' : off}
          />
          <Kpi
            label="Clock offset"
            testId="kpi-clock"
            value={c ? `${c.offset_ms > 0 ? '+' : ''}${c.offset_ms.toFixed(1)} ms` : dash}
            tone={c?.suspect ? 'warn' : undefined}
            sub={
              c
                ? c.suspect
                  ? c.synced === false
                    ? '▲ not synced · traces marked'
                    : '▲ suspect · traces marked'
                  : `✓ ${c.synced ? 'synced' : c.source}${c.est_error_us !== undefined ? ` · ±${(c.est_error_us / 1000).toFixed(1)} ms` : ''}`
                : off
            }
          />
        </div>
        {r && (
          <div className="mt-4">
            <RelayNextStep n={n} testId="node-relay-next-step" />
          </div>
        )}
        {relayOn && r && (
          <dl className="mt-4 grid gap-x-6 gap-y-1 border-t border-line pt-3 text-[13px] sm:grid-cols-2" data-testid="node-versions">
            <div className="flex flex-wrap gap-x-2">
              <dt className="text-muted">Peers list</dt>
              <dd className="tabular font-mono text-xs" data-testid="node-peers-version" data-applied={r.peers_version_acked >= r.peers_version}>
                v{r.peers_version_acked} of v{r.peers_version}{' '}
                {r.peers_version_acked >= r.peers_version ? <span className="text-live">✓ applied</span> : <span className="text-warn">○ not acknowledged yet</span>}
                {r.peers_total !== undefined && r.peers_acked !== undefined && r.peers_total > 0 && (
                  <span className={r.peers_acked < r.peers_total ? 'text-warn' : 'text-muted'}> · trusted by {r.peers_acked} of {r.peers_total}</span>
                )}
              </dd>
            </div>
            <div className="flex flex-wrap gap-x-2">
              <dt className="text-muted">Routes</dt>
              <dd className="tabular font-mono text-xs" data-testid="node-routes-version" data-applied={r.routes_version_acked >= r.routes_version}>
                {r.routes_version === 0 ? (
                  <span className="text-muted">never had legs</span>
                ) : (
                  <>
                    v{r.routes_version_acked} of v{r.routes_version}{' '}
                    {r.routes_version_acked >= r.routes_version ? <span className="text-live">✓ applied</span> : <span className="text-warn">○ not applied yet</span>}
                  </>
                )}
              </dd>
            </div>
          </dl>
        )}
      </NetPanel>

      {issues.length > 0 && (
        <NetPanel title="Why degraded" testId="node-why">
          <ul className="flex flex-col gap-2 text-sm">
            {issues.map((x) => (
              <li key={x} className="flex gap-2">
                <span className="text-warn" aria-hidden>
                  ▲
                </span>
                <span>
                  <b className="font-medium">{x}</b>
                  <span className="text-text-soft"> — {issueWhy(x, n)}</span>
                </span>
              </li>
            ))}
          </ul>
        </NetPanel>
      )}

      {relayOn && n.status !== 'revoked' && !n.roles?.includes('integrations') && <RolesRelay n={n} live={live} routes={routes} />}
      {n.status !== 'revoked' && !n.roles?.includes('integrations') && <NodeFailoverPanel n={n} />}
      {n.status !== 'revoked' && !n.roles?.includes('integrations') && <PrivateAddressPanel n={n} />}

      {relayOn && <History n={n} live={live} />}
    </div>
  )
}

const STEP_TEXT: Record<NodeMetrics['step_s'], string> = { 5: '5 s', 60: '1 min', 900: '15 min', 3600: '1 h' }

function History({ n, live }: { n: NodeInfo; live: RelayLive }) {
  const [range, setRange] = useState<StatsRange>('15m')
  const path = `/api/admin/nodes/${encodeURIComponent(n.id)}/metrics?range=${range}`
  const q = useQuery({
    queryKey: relayKeys.nodeMetrics(n.id, range),
    queryFn: async () => {
      try {
        const r = await getAsOf<NodeMetrics>(path)
        noteRelayAsOf(r.asOf, r.at)
        return r
      } catch (e) {
        noteRelayFailure(e)
        throw e
      }
    },
    retry: (k, e) => !(e instanceof ApiError && e.status < 500) && k < 1,
    // 15 min moves every 5 s; longer ranges once a minute
    refetchInterval: range === '15m' ? POLL_MS : 60_000,
  })
  const m = q.data?.data
  const label = RANGES.find((x) => x.id === range)!.label
  return (
    <NetPanel
      title="History"
      testId="node-history"
      aside={
        <span role="group" aria-label="Time range" className="flex flex-wrap gap-1">
          {RANGES.map((x) => (
            <button
              key={x.id}
              type="button"
              aria-pressed={range === x.id}
              data-testid={`node-range-${x.id}`}
              onClick={() => setRange(x.id)}
              className={`tabular inline-flex min-h-8 items-center rounded-sm border px-2.5 text-xs max-md:min-h-[44px] ${range === x.id ? 'border-live/50 bg-live/[.12] text-live' : 'border-line-2 text-muted hover:text-text'}`}
            >
              {x.label}
            </button>
          ))}
        </span>
      }
    >
      {q.isPending ? (
        <SkeletonRows rows={4} cols={4} />
      ) : q.error && !q.data ? (
        <ErrorBox testId="node-history-error" text={errorText(path, q.error)} busy={q.isFetching} onRetry={() => void q.refetch()} />
      ) : !m?.points.length ? (
        <Empty title={`No data in the last ${label}`} testId="node-history-empty">
          {range === '15m' ? 'The 15-minute view comes from the center’s memory and starts over when the center restarts; the node reports every 5 s.' : 'The node sent no reports in this window, or it is older than the retention.'}
        </Empty>
      ) : (
        <Charts n={n} m={m} label={label} />
      )}
      {live.available && m && (
        <p className="mt-3 text-xs text-muted" data-testid="node-history-step">
          {m.step_s === 5 ? '15 min from the center’s memory at a 5 s step; lost when the center restarts.' : `From the database at a ${STEP_TEXT[m.step_s]} step (averages; legs: the highest).`}{' '}
          {plural(m.points.length, 'point')}.
        </p>
      )}
    </NetPanel>
  )
}

function Charts({ n, m, label }: { n: NodeInfo; m: NodeMetrics; label: string }) {
  const ax = onAxis<LoadPoint>(m.points, m.step_s * 1000)
  const col = (k: keyof LoadPoint) => ax.t.map((_, i) => {
    const v = ax.at(i)?.[k]
    return typeof v === 'number' ? v : null
  })
  const cpu = col('cpu_pct')
  const mem = col('mem_pct')
  const rx = col('rx_bps')
  const tx = col('tx_bps')
  const legs = col('legs_up')
  // LoadPoint.legs (API-UI): the legs that are not up show as the gap between the lines
  const legsAll = col('legs')
  const hasAll = legsAll.some((v) => v != null)
  const off = col('clock_offset_ms')
  const big = Math.max(20, ...off.map((v) => Math.abs(v ?? 0)))
  const l = n.status === 'online' ? n.load : undefined
  const lg = nodeLegs(n)
  const c = n.status === 'online' ? n.clock : undefined
  const short = (v: number) => (v >= 1e9 ? `${(v / 1e9).toFixed(1)}G` : v >= 1e6 ? `${Math.round(v / 1e6)}M` : v >= 1e3 ? `${Math.round(v / 1e3)}k` : `${Math.round(v)}`)
  return (
    <div className="grid gap-x-8 gap-y-6 lg:grid-cols-2" data-testid="node-charts" data-points={m.points.length}>
      <LineChart
        title="CPU and memory"
        unit="%"
        t={ax.t}
        min={0}
        max={100}
        refLine={85}
        refLabel="85% degraded"
        series={[
          { name: 'CPU', values: cpu, stroke: 'stroke-text' },
          { name: 'memory', values: mem, stroke: 'stroke-muted', dash: '5 3' },
        ]}
        fmt={(v) => `${Math.round(v)}`}
        label={`CPU and memory of ${n.name}, last ${label}`}
        now={l ? `${Math.round(l.cpu_pct)}% · ${Math.round(l.mem_pct)}%` : undefined}
        testId="chart-cpu"
      />
      <LineChart
        title="Traffic"
        unit="b/s"
        t={ax.t}
        series={[
          { name: '↓ in', values: rx, stroke: 'stroke-muted', dash: '5 3' },
          { name: '↑ out', values: tx, stroke: 'stroke-text' },
        ]}
        fmt={short}
        label={`Traffic of ${n.name}, last ${label}`}
        now={l ? `↓${bps(l.rx_bps)} ↑${bps(l.tx_bps)}` : undefined}
        testId="chart-traffic"
      />
      <LineChart
        title="Legs up"
        t={ax.t}
        min={0}
        max={Math.max(1, lg.legs, ...legs.map((v) => v ?? 0), ...legsAll.map((v) => v ?? 0))}
        series={hasAll ? [{ name: 'legs', values: legsAll, stroke: 'stroke-muted', dash: '5 3' }, { name: 'up', values: legs, stroke: 'stroke-text' }] : [{ name: 'legs up', values: legs, stroke: 'stroke-text' }]}
        fmt={(v) => `${Math.round(v)}`}
        label={hasAll ? `Relay legs and legs up on ${n.name}, last ${label}` : `Relay legs up on ${n.name}, last ${label}`}
        now={n.status === 'online' && lg.known ? `${lg.legsUp}/${lg.legs}` : undefined}
        testId="chart-legs"
      />
      <LineChart
        title="Clock offset"
        unit="ms"
        t={ax.t}
        min={-big}
        max={big}
        refLine={[20, -20]}
        refLabel="±20 ms suspect"
        series={[{ name: 'offset', values: off, stroke: c?.suspect ? 'stroke-warn' : 'stroke-text' }]}
        fmt={(v) => `${v > 0 ? '+' : ''}${Math.round(v)}`}
        label={`Clock offset of ${n.name} against the center, last ${label}`}
        now={c ? `${c.offset_ms > 0 ? '+' : ''}${c.offset_ms.toFixed(1)} ms` : undefined}
        testId="chart-clock"
      />
    </div>
  )
}
